import React, { useEffect, useState } from 'react';
import { useDashboardDomainContext } from "../../DashboardDomainProvider";
import { ContextMenuControl, IconControl, Loading } from 'spark-core-dx/components';
import IconSelectorDialog from '../dialogs/IconSelectorDialog';
import moment from 'moment-timezone';
import './DashboardHeader.scss';

const DASHBOARD_STATUS_MENU_X_OFFSET = 56;
const DASHBOARD_STATUS_MENU_ESTIMATED_WIDTH = 160;
const DASHBOARD_STATUS_MENU_RIGHT_NUDGE = -16;

const DashboardHeader = ({
  bottomRightActions = null,
  leftStatusMenus = [],
  topRightMenus = [],
  topRightActions = null,
  statusLabel = "",
  statusDetail = "",
  statusTone = "",
  onStatusClick = null,
  statusMenus = [],
  statusFooter = null,
  leftFooter = null,
  standaloneTitle = "",
  standaloneDescription = "",
  standaloneSubtitle = "",
  standaloneIcon = "schema",
  hideTemplateLabel = false,
  showDefaultPublicIcon = true,
}) => {
  const dashboardDomain = useDashboardDomainContext() ?? {};
  const {
    activeDashboard,
    selectedPageData,
    dashboardLib,
    refreshAllVisuals,
    isRefreshingVisuals,
    mode,
    isOwner,
    isModelDesigner,
    activeDraftWorkspaceSummary,
    selectedPageVisuals = [],
  } = dashboardDomain;
  const isStandalone = String(standaloneTitle ?? "").trim().length > 0;

  const isViewMode = !isStandalone && mode === 'view';
  const [isIconDialogOpen, setIsIconDialogOpen] = useState(false);

  const {
    DraftDetails = {},
    PublishedDetails = {},
    UpdatedDate: draftDate,
    State: {
      PublishedDate: publishedDate,
      IsPublished: isPublished = false,
      IsPublic: isPublic = false,
      IsTemplate: isTemplate = false,
    } = {},
  } = activeDashboard ?? {};

  const activeDetails = isStandalone
    ? {
        Name: standaloneTitle,
        Description: standaloneDescription,
        Icon: standaloneIcon,
      }
    : isViewMode
      ? (isPublished && PublishedDetails ? PublishedDetails : DraftDetails)
      : DraftDetails;

  const [formName, setFormName] = useState(activeDetails?.Name ?? "");
  const [formDescription, setFormDescription] = useState(activeDetails?.Description ?? "");
  const [openStatusMenuKey, setOpenStatusMenuKey] = useState(false);
  const [statusMenuStyle, setStatusMenuStyle] = useState(null);

  useEffect(() => {
    setFormName(activeDetails?.Name ?? "");
    setFormDescription(activeDetails?.Description ?? "");
  }, [activeDetails?.Name, activeDetails?.Description]);

  useEffect(() => {
    setOpenStatusMenuKey(false);
    setStatusMenuStyle(null);
  }, [activeDashboard?._id, isStandalone]);

  const formatDate = (date) => {
    if (!date) return "";
    return moment.tz(date, moment.tz.guess()).format('MM/DD/YY [at] h:mm a z');
  };

  if (!isStandalone && !activeDashboard?._id) {
    return null;
  }

  const currentIcon = activeDetails?.Icon || "dashboard";
  const dashboardStateLabel = !isViewMode
    ? "Draft"
    : isPublished
      ? "Published"
      : "Draft";
  const dashboardStateTitle = dashboardStateLabel === "Published"
    ? `Published on ${formatDate(publishedDate)}`
    : "Draft";
  const hasCustomStatus = Boolean(String(statusLabel ?? "").trim() || String(statusDetail ?? "").trim());
  const showVisualRefreshControl =
    !isStandalone &&
    !isModelDesigner &&
    !!selectedPageData?.Name;
  const canRefreshVisuals = Array.isArray(selectedPageVisuals) && selectedPageVisuals.length > 0;
  const hasLeftStatusMenus = Array.isArray(leftStatusMenus) && leftStatusMenus.length > 0;
  const hasTopRightMenus = Array.isArray(topRightMenus) && topRightMenus.length > 0;
  const hasStatusMenus = Array.isArray(statusMenus) && statusMenus.length > 0;
  const isStatusClickable = typeof onStatusClick === "function";
  const activeStatusMenu = [
    ...(Array.isArray(leftStatusMenus) ? leftStatusMenus : []),
    ...(Array.isArray(topRightMenus) ? topRightMenus : []),
    ...(Array.isArray(statusMenus) ? statusMenus : []),
  ]
    .find((menu) => String(menu?.key ?? "") === String(openStatusMenuKey ?? "")) ?? null;
  const handleOpenStatusMenu = (event, key) => {
    if (!key) {
      return;
    }

    const nextKey = String(openStatusMenuKey ?? "") === String(key) ? false : key;
    const anchorElement = event?.currentTarget?.querySelector?.(".dashboard-state-chip") ?? event?.currentTarget;
    const rect = anchorElement?.getBoundingClientRect?.();
    if (rect) {
      const isTopMenuTrigger = event?.currentTarget?.classList?.contains("dashboard-status-row--top");
      const rightAnchoredLeft = rect.right - DASHBOARD_STATUS_MENU_ESTIMATED_WIDTH + DASHBOARD_STATUS_MENU_RIGHT_NUDGE;
      const defaultAnchoredLeft = rect.left + DASHBOARD_STATUS_MENU_X_OFFSET;
      const maxLeft = Math.max(16, window.innerWidth - DASHBOARD_STATUS_MENU_ESTIMATED_WIDTH - 16);
      const nextLeft = Math.min(
        Math.max(16, isTopMenuTrigger ? rightAnchoredLeft : defaultAnchoredLeft),
        maxLeft
      );
      setStatusMenuStyle({
        left: nextLeft,
        top: rect.bottom + 6,
      });
    }
    setOpenStatusMenuKey(nextKey);
  };

  const renderStatusMenuOptions = (options = []) => (
    <div className="options-wrapper dashboard-status-options">
      {options.map((option) => (
        <div
          key={`${option?.text ?? "option"}-${option?.icon ?? "icon"}`}
          className={`option${option?.disabled ? " disabled" : ""}${option?.danger ? " danger" : ""}`}
          onClick={option?.disabled
            ? undefined
            : () => {
              setOpenStatusMenuKey(false);
              option?.onClick?.();
            }}
        >
          {option?.icon ? <IconControl iconKey={option?.icon} /> : null}
          <span className="dashboard-status-option__text">{option?.text}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className={`dashboard-header ${isViewMode ? "view-mode" : "design-mode"}`}>
      {!hideTemplateLabel && isTemplate && <div className="template-label">TEMPLATE</div>}

      <div className="header-top-section">
        <div className="header-left-section">
          <IconControl
            className="dashboard-icon"
            iconKey={currentIcon}
            color={"var(--primary-text)"}
            onClick={!isStandalone && !isViewMode ? () => setIsIconDialogOpen(true) : null}
          />
          {isStandalone ? (
            <div className="dashboard-title dashboard-title--readonly">
              {standaloneTitle}
            </div>
          ) : (
            <input
              disabled={isViewMode}
              className="dashboard-title"
              value={formName}
              placeholder="Dashboard Name"
              onChange={(e) => setFormName(e.target.value)}
              onBlur={(e) => {
                if (!isViewMode && e.target.value !== (DraftDetails?.Name ?? "")) {
                  dashboardLib.handleDashNameChange(e.target.value);
                }
              }}
            />
          )}
        </div>

        <div className="header-right-section">
          {isStandalone ? (
            <span className="dashboard-published-info">
              {standaloneSubtitle || "Reusable Data Model Designer"}
            </span>
          ) : isModelDesigner ? (
            <span className="dashboard-published-info dashboard-published-info--model-designer">
              Model Designer
            </span>
          ) : selectedPageData?.Name ? (
            <>
              {!isViewMode && activeDraftWorkspaceSummary?.label ? (
                <span className="dashboard-published-info dashboard-published-info--workspace">
                  Model Workspace: {activeDraftWorkspaceSummary.label} -
                </span>
              ) : null}
              <span className="dashboard-published-info">
                Page: {selectedPageData.Name}
              </span>
              {showVisualRefreshControl ? (
                <button
                  type="button"
                  className="dashboard-header-action-button refresh-all"
                  title={isRefreshingVisuals ? "Refreshing visuals..." : "Refresh all visuals"}
                  onClick={() => refreshAllVisuals?.()}
                  disabled={!canRefreshVisuals || isRefreshingVisuals}
                >
                  {isRefreshingVisuals ? (
                    <Loading type="spinner-2" size="1rem" className="tiny-loading" />
                  ) : (
                    <IconControl iconKey="refresh" title="Refresh all visuals" />
                  )}
                </button>
              ) : null}
            </>
          ) : null}
          {hasTopRightMenus ? (
            <div className="dashboard-top-status-panel">
                {topRightMenus.map((menu) => {
                  const menuKey = String(menu?.key ?? "");
                  const isMenuClickable = menuKey.length > 0;
                  const showCaret = (menu?.showCaret !== false) && isMenuClickable;
                  const detailTone = menu?.detailTone ?? menu?.tone ?? "";
                  const labelTone = menu?.labelTone ?? menu?.tone ?? "";

                return (
                  <div
                    key={menuKey || menu?.label || menu?.detail}
                    className={`dashboard-status-row dashboard-status-row--menu dashboard-status-row--top${isMenuClickable ? " is-clickable" : ""}`}
                    role={isMenuClickable ? "button" : undefined}
                    tabIndex={isMenuClickable ? 0 : undefined}
                    title={menu?.title || menu?.label || menu?.detail || undefined}
                    onClick={isMenuClickable ? (event) => handleOpenStatusMenu(event, menuKey) : undefined}
                    onKeyDown={isMenuClickable
                      ? (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleOpenStatusMenu(event, menuKey);
                        }
                      }
                      : undefined}
                  >
                    {menu?.detail ? (
                      <span className={`dashboard-draft-info${detailTone ? ` dashboard-draft-info--${detailTone}` : ""}${menu?.detailClassName ? ` ${menu.detailClassName}` : ""}`}>
                        {menu.detail}
                      </span>
                    ) : null}
                    {menu?.label ? (
                      <span
                        className={`dashboard-state-chip${labelTone ? ` dashboard-state-chip--${labelTone}` : ""}${menu?.labelClassName ? ` ${menu.labelClassName}` : ""}`}
                        title={menu.label}
                      >
                        <span className="dashboard-state-chip__text">{menu.label}</span>
                        {showCaret ? (
                          <IconControl className="dashboard-state-chip__caret" iconKey="arrowdropdown" />
                        ) : null}
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}
          {topRightActions}
          {showDefaultPublicIcon && isOwner && isPublished && (!isViewMode || (isViewMode && isPublic)) && (
            <IconControl
              className={`public-icon ${isPublic ? "" : "off-mode"}`}
              title={isPublic ? "Public" : "Private"}
              iconKey={isPublic ? "public" : "publicoff"}
            />
          )}
        </div>
      </div>

      <div className="header-bottom-section">
        <div className="header-left-section">
          <div className="dashboard-left-meta-stack">
            {isStandalone ? (
              <div className={`dashboard-description dashboard-description--readonly ${!standaloneDescription ? 'no-description' : ''}`}>
                {standaloneDescription}
              </div>
            ) : (
              <input
                disabled={isViewMode}
                className={`dashboard-description ${isViewMode && !activeDetails?.Description ? 'no-description' : ''}`}
                value={formDescription}
                placeholder="Dashboard Description"
                onChange={(e) => setFormDescription(e.target.value)}
                onBlur={(e) => {
                  if (!isViewMode && e.target.value !== (DraftDetails?.Description ?? "")) {
                    dashboardLib.handleDashDescriptionChange(e.target.value);
                  }
                }}
              />
            )}
            {leftFooter ? (
              <div className="dashboard-left-footer">
                {leftFooter}
              </div>
            ) : null}
            {hasLeftStatusMenus ? (
              <div className="dashboard-left-status-panel">
                {leftStatusMenus.map((menu) => {
                  const menuKey = String(menu?.key ?? "");
                  const isMenuClickable = menuKey.length > 0;
                  const showCaret = (menu?.showCaret !== false) && isMenuClickable;
                  const detailTone = menu?.detailTone ?? menu?.tone ?? "";
                  const labelTone = menu?.labelTone ?? menu?.tone ?? "";

                  return (
                    <div
                      key={menuKey || menu?.label || menu?.detail}
                      className={`dashboard-status-row dashboard-status-row--menu dashboard-status-row--left${isMenuClickable ? " is-clickable" : ""}`}
                      role={isMenuClickable ? "button" : undefined}
                      tabIndex={isMenuClickable ? 0 : undefined}
                      title={menu?.title || menu?.label || menu?.detail || undefined}
                      onClick={isMenuClickable ? (event) => handleOpenStatusMenu(event, menuKey) : undefined}
                      onKeyDown={isMenuClickable
                        ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleOpenStatusMenu(event, menuKey);
                          }
                        }
                        : undefined}
                    >
                      {menu?.detail ? (
                        <span className={`dashboard-draft-info${detailTone ? ` dashboard-draft-info--${detailTone}` : ""}${menu?.detailClassName ? ` ${menu.detailClassName}` : ""}`}>
                          {menu.detail}
                        </span>
                      ) : null}
                      {menu?.label ? (
                        <span
                          className={`dashboard-state-chip${labelTone ? ` dashboard-state-chip--${labelTone}` : ""}${menu?.labelClassName ? ` ${menu.labelClassName}` : ""}`}
                          title={menu.label}
                        >
                          <span className="dashboard-state-chip__text">{menu.label}</span>
                          {showCaret ? (
                            <IconControl className="dashboard-state-chip__caret" iconKey="arrowdropdown" />
                          ) : null}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
        <div className="header-right-section">
          <div className="dashboard-status-panel-container">
            <div className={`dashboard-status-panel${hasStatusMenus ? " has-menus" : ""}${isStatusClickable ? " is-clickable" : ""}`}>
              {hasStatusMenus ? (
                <>
                  {statusMenus.map((menu) => {
                  const menuKey = String(menu?.key ?? "");
                  const isMenuClickable = menuKey.length > 0;
                  const showCaret = (menu?.showCaret !== false) && isMenuClickable;
                  const detailTone = menu?.detailTone ?? menu?.tone ?? "";
                  const labelTone = menu?.labelTone ?? menu?.tone ?? "";

                  return (
                    <div
                      key={menuKey || menu?.label || menu?.detail}
                      className={`dashboard-status-row dashboard-status-row--menu${isMenuClickable ? " is-clickable" : ""}`}
                      role={isMenuClickable ? "button" : undefined}
                      tabIndex={isMenuClickable ? 0 : undefined}
                      title={menu?.title || menu?.label || menu?.detail || undefined}
                      onClick={isMenuClickable ? (event) => handleOpenStatusMenu(event, menuKey) : undefined}
                      onKeyDown={isMenuClickable
                        ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleOpenStatusMenu(event, menuKey);
                          }
                        }
                        : undefined}
                    >
                      {menu?.detail ? (
                        <span className={`dashboard-draft-info${detailTone ? ` dashboard-draft-info--${detailTone}` : ""}${menu?.detailClassName ? ` ${menu.detailClassName}` : ""}`}>
                          {menu.detail}
                        </span>
                      ) : null}
                      {menu?.label ? (
                        <span
                          className={`dashboard-state-chip${labelTone ? ` dashboard-state-chip--${labelTone}` : ""}${menu?.labelClassName ? ` ${menu.labelClassName}` : ""}`}
                          title={menu.label}
                        >
                          <span className="dashboard-state-chip__text">{menu.label}</span>
                          {showCaret ? (
                            <IconControl className="dashboard-state-chip__caret" iconKey="arrowdropdown" />
                          ) : null}
                        </span>
                      ) : null}
                    </div>
                  );
                  })}
                </>
              ) : (
                <div
                  className={`dashboard-status-row${isStatusClickable ? " is-clickable" : ""}`}
                  role={isStatusClickable ? "button" : undefined}
                  tabIndex={isStatusClickable ? 0 : undefined}
                  title={isStatusClickable ? "Check virtual DB status" : undefined}
                  onClick={isStatusClickable ? onStatusClick : undefined}
                  onKeyDown={isStatusClickable
                    ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onStatusClick();
                      }
                    }
                    : undefined}
                >
                  {hasCustomStatus ? (
                    <>
                      {statusDetail ? (
                        <span
                          className={`dashboard-draft-info${statusTone ? ` dashboard-draft-info--${statusTone}` : ""}`}
                        >
                          {statusDetail}
                        </span>
                      ) : null}
                      {statusLabel ? (
                        <span
                          className={`dashboard-state-chip${statusTone ? ` dashboard-state-chip--${statusTone}` : ""}`}
                          title={statusLabel}
                        >
                          {statusLabel}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <>
                      {!isViewMode && (
                        <span className="dashboard-draft-info">
                          {`Draft Saved on ${formatDate(draftDate)}`}
                        </span>
                      )}
                      {isViewMode && isPublished && (
                        <span
                          className="dashboard-draft-info"
                        >
                          {`Published on ${formatDate(publishedDate)}`}
                        </span>
                      )}
                      <span
                        className="dashboard-state-chip"
                        title={dashboardStateTitle}
                      >
                        {dashboardStateLabel}
                      </span>
                    </>
                  )}
                </div>
              )}
              {!hasStatusMenus && bottomRightActions ? (
                <div className="dashboard-header-actions dashboard-header-actions--status">
                  {bottomRightActions}
                </div>
              ) : null}
            </div>
            {statusFooter ? (
              <div className="dashboard-status-footer">
                {statusFooter}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {!isStandalone ? (
        <IconSelectorDialog
          isActive={isIconDialogOpen}
          closeDialog={() => setIsIconDialogOpen(false)}
        />
      ) : null}
      {activeStatusMenu ? (
        <ContextMenuControl
          openContextMenu={openStatusMenuKey}
          setOpenContextMenu={setOpenStatusMenuKey}
          contextMenuStyle={statusMenuStyle}
        >
          {renderStatusMenuOptions(activeStatusMenu?.items)}
        </ContextMenuControl>
      ) : null}
    </div>
  );
};

export default DashboardHeader;
